import { useContext, useEffect, useState } from "react";
import FloatingPanelService, {
  FloatingPanelMetadata
} from "../../../../shared/FloatingPanel";
import ClientService from "../../../../../services/ClientService";
import CompanyService from "../../../../../services/CompanyService";
import { TranslationService } from "../../../../../services/TranslationService";
import { hasValuesLength } from "../../../../../utils/Utils";
import ToastContext from "../../../../shared/bootstrap/Toast";
import {
  AutocompleteSelect,
  DatePickerEditor,
  HasValidationEditor,
  HasValidationEditorProps,
  TextAreaBigEditor,
  TextEditorNumber
} from "../../../../shared/components/Editors";
import { FloatingPanelFooter } from "../../../../shared/components/FloatingPanelFooter";
import {
  RequiredManager,
  ValidationMessage
} from "../../../../shared/RequieredManager";
import Dropdown from "../../../../shared/components/Dropdown";
import { ScheduleTaskEdit } from "../../../entities/scheduledTask/ScheduleTaskEdit";
import { ScheduledTaskService } from "../../../../../services/ScheduledTaskService";
import SwitchButton from "../../../../shared/components/SwitchButton";
import {
  capitalize,
  formatDateDigitsMin
} from "../../../../../utils/FormatUtils";
import TableContext from "../../../TableContext";
import { ScheduledTaskTableContextValues } from "../ScheduledTaskTableProvider";
import { ScheduledTask } from "../../../entities/scheduledTask/ScheduledTaskList";
import { MapIfHasHours, restTimezone } from "../../../../../utils/ParseUtils";

type NewTaskProp = {
  onNewTask?: () => void;
  groupId?: number;
  scheduleTask?: ScheduledTask;
  readonly?: boolean;
} & (
  | { personId: string; personName: string }
  | { personId?: undefined; personName?: undefined }
);

const NewScheduledTaskForm = FloatingPanelMetadata(
  {
    title: TranslationService.translate.NewScheduledTask,
    width: 700,
    height: 650
  },
  function NewTaskFromImpl({
    onNewTask = undefined,
    personId = undefined,
    personName = undefined,
    groupId,
    scheduleTask,
    readonly = false
  }: NewTaskProp) {
    const loggedUserId = CompanyService.getUserid() ?? "";
    const { translate } = TranslationService;
    const { showToast } = useContext(ToastContext);

    const tableContext = useContext(TableContext);
    const { reload } = tableContext as ScheduledTaskTableContextValues;
    onNewTask = onNewTask ?? reload;

    const mapScheduledTaskToModel = (scheduleTask: ScheduledTask) => {
      var editTask = new ScheduleTaskEdit(
        loggedUserId,
        personId,
        groupId?.toString()
      );
      editTask.taskId = scheduleTask.ScheduledTaskID;
      editTask.responsibleUserID = scheduleTask.ResponsibleUserID.toString();
      editTask.activityTypeId = scheduleTask.ActivityTypeID;
      editTask.creatorUserID = scheduleTask.CreatorUserID.toString();
      editTask.scheduledTaskID = scheduleTask.ScheduledTaskID;
      editTask.dueDays = scheduleTask.dueDays;
      editTask.description = scheduleTask.Description;
      editTask.created = scheduleTask.Created;
      editTask.validatorUserID = scheduleTask.ValidatorUserID;
      editTask.repeatPeriod = scheduleTask.RepeatPeriod;
      editTask.repeatFrequency = scheduleTask.RepeatFrequency;
      editTask.personId = scheduleTask.Person
        ? scheduleTask.Person.PersonId.toString()
        : scheduleTask.PersonID.toString();

      if (scheduleTask.RememberDays && scheduleTask.RememberHour) {
        editTask.rememberMe = true;
      }

      if (scheduleTask.RepeatPeriod === 1) {
        editTask.weekday = scheduleTask.RepeatFrequency;
      }

      if (scheduleTask.RepeatPeriod === 2) {
        editTask.monthday = scheduleTask.RepeatFrequency;
      }

      editTask.repeatEnd = scheduleTask.RepeatEnd?.toString();
      editTask.calendar = formatDateDigitsMin(
        scheduleTask.RepeatEnd?.toString()
      );
      editTask.rememberDays = scheduleTask.RememberDays;
      editTask.notifyCompleted = scheduleTask.NotifyCompleted;
      editTask.rememberHour =
        new Date(scheduleTask.RememberHour?.replace("Z", ""))?.getHours() ?? 8;
      return editTask;
    };

    const state = scheduleTask
      ? mapScheduledTaskToModel(scheduleTask)
      : new ScheduleTaskEdit(loggedUserId, personId, groupId?.toString());
    const [taskEdit, setTaskEdit] = useState(state);

    const [isSaving, setIsSaving] = useState(false);
    const [clientMandatoryMessage, setClientMandatoryMessage] = useState(false);
    const requiredManager = new RequiredManager();
    const validationFunctionArray: (() => boolean)[] = [];
    const getUserOptions =
      CompanyService.getUsers()?.map((x) => ({
        value: x.Id,
        text: x.Value ?? "",
        key: x.Id
      })) ?? [];
    const getActivityTypeOptions =
      CompanyService.getActivityTypes()
        .filter((x) => x.Task)
        .map((x) => ({
          value: x.ActivityTypeID,
          text: x.ActivityTypeName ?? ""
        })) ?? [];
    const getReminderHourOptions = [...Array(24).keys()].map((x) => ({
      value: x.toString(),
      text: (x + ":00").padStart(5, "0")
    }));
    const dayList = [...Array(32).keys()]
      .filter((x) => x !== 0)
      .map((x) => ({ value: x, text: x.toString() }));

    const taskFieldChange =
      (propName: keyof ScheduleTaskEdit) =>
      (newValue: string | number | boolean | undefined | void) => {
        setTaskEdit((x) => ({ ...x, [propName]: newValue }));
      };

    const clientSelectOptions = async (inputValue: string) => {
      let list: Array<{ value: string; label: string }> = [];
      const minInputLength = 2;
      if (inputValue.length < minInputLength) {
        return list;
      }
      const result = await ClientService.search(inputValue);
      if (!(result instanceof Error)) {
        list = result.list.map((x) => ({
          value: x.id.toString(),
          label: x.name
        }));
      }
      return list;
    };

    const canSaveTask = () => {
      setClientMandatoryMessage(!taskEdit.personId);
      return (
        requiredManager.validate() &&
        validationFunctionArray.filter((x) => !x()).length === 0 &&
        taskEdit.personId
      );
    };

    const saveTask = async () => {
      if (isSaving || !canSaveTask()) {
        return;
      }
      setIsSaving(true);

      if (!taskEdit.groupId && groupId) {
        taskEdit.groupId = groupId.toString();
      }
      const result = await ScheduledTaskService.setScheduledTask(
        taskEdit,
        loggedUserId
      );
      if (result instanceof Error) {
        showToast(translate.ErrorProcessingRequest, undefined, "danger");
        setIsSaving(false);
      } else {
        showToast(translate.TaskSaved, undefined, "success");
        setTaskEdit(new ScheduleTaskEdit(loggedUserId?.toString()));
        setIsSaving(false);
        onNewTask && onNewTask();
        FloatingPanelService.hidePanel();
      }
    };

    //the same logic that V1 has
    const weekend = [
      { value: 2, text: translate.WeekMonday },
      { value: 3, text: translate.WeekTuesday },
      { value: 4, text: translate.WeekWednesday },
      { value: 5, text: translate.WeekThursday },
      { value: 6, text: translate.WeekFriday },
      { value: 7, text: translate.WeekSaturday },
      { value: 1, text: translate.WeekSunday }
    ];

    const periodsList = [
      { value: 0, text: translate.Daily },
      { value: 1, text: translate.Weekly },
      { value: 2, text: translate.Monthly }
    ];

    const onDelete = async () => {
      if (isSaving) {
        return;
      }

      setIsSaving(true);
      const result = await ScheduledTaskService.deleteScheduledTask(
        taskEdit.scheduledTaskID
      );

      if (result instanceof Error) {
        showToast(translate.ErrorProcessingRequest, undefined, "danger");
        return;
      }
      setIsSaving(false);
      onNewTask && onNewTask();
      FloatingPanelService.hidePanel();
    };

    useEffect(() => {
      if (!taskEdit.rememberMe) {
        taskFieldChange("rememberHour")("");
      }
    }, [taskEdit.rememberMe]);

    return (
      <>
        <div className="tabBody">
          <div className="row align-items-center mb-3 g-0">
            <div className="col-2 pe-2">
              <label className="form-label mb-2">{translate.Client}</label>
            </div>
            <div
              className={
                "pe-2 col-" + (getActivityTypeOptions.length ? "4" : "10")
              }
            >
              {personId === undefined && (
                <AutocompleteSelect
                  loadOptions={clientSelectOptions}
                  onChange={(clientId) => {
                    taskFieldChange("personId")(clientId?.value);
                    setClientMandatoryMessage(!clientId);
                  }}
                  defaultVal={
                    scheduleTask?.Person
                      ? {
                          value: scheduleTask.Person.PersonId.toString(),
                          label: scheduleTask.Person.Name
                        }
                      : undefined
                  }
                />
              )}
              {personId !== undefined && <p className="mb-0">{personName}</p>}
              {clientMandatoryMessage && (
                <p className="text-danger mb-0 mt-1">
                  {translate.ClientMandatoryMessage}
                </p>
              )}
            </div>
            {hasValuesLength(getActivityTypeOptions) && (
              <>
                <div className="col-2">
                  <label className="form-label mb-2 me-2">
                    {translate.ActivityType2}
                  </label>
                </div>
                <div className="col-4">
                  <Dropdown
                    optionLabel={translate.SelectIt}
                    onChange={requiredManager.makeRequiredIf(
                      CompanyService.getSetting("mandatorytasktype"),
                      taskFieldChange("activityTypeId"),
                      "activitytype"
                    )}
                    items={getActivityTypeOptions}
                    defaultValue={taskEdit.activityTypeId}
                    readonly={readonly}
                  />

                  {CompanyService.getSetting("mandatorytasktype") && (
                    <ValidationMessage
                      onChange={requiredManager.getValidation(
                        "mandatorytasktype"
                      )}
                      message={translate.RequiredField}
                      defaultValue={taskEdit.activityTypeId}
                    />
                  )}
                </div>
              </>
            )}
          </div>
          <div className="row align-items-center mb-3 g-0">
            {hasValuesLength(getUserOptions) && (
              <>
                <div className="col-2">
                  <label className="form-label mb-2">
                    {translate.Responsible}
                  </label>
                </div>
                <div className="col-4 pe-2">
                  <Dropdown
                    optionLabel={translate.NotAssigned}
                    onChange={taskFieldChange("responsibleUserID")}
                    items={getUserOptions}
                    defaultValue={CompanyService.getUserid()}
                    readonly={readonly}
                  />
                </div>
              </>
            )}
            <div className="col-2">
              <label className="form-label mb-2 px-0">
                {capitalize(translate.Days)}
              </label>
            </div>
            <div className="col-4">
              <TextEditorNumber
                onChange={(e) => {
                  if (e) {
                    taskFieldChange("dueDays")(e);
                  } else {
                    taskFieldChange("dueDays")(undefined);
                  }
                }}
                defaultValue={taskEdit.dueDays?.toString()}
                disabled={readonly}
              />
              <ValidationMessage
                onChange={requiredManager.makeRequiredWithId(
                  (value: number) => taskFieldChange("dueDays")(value),
                  "dueDays"
                )}
                defaultValue={taskEdit.dueDays}
                validationMethod={(value: number | undefined) => {
                  return (value ?? 0) > 0;
                }}
              />
            </div>
          </div>

          {CompanyService.getSetting("enabletaskvalidator") &&
            hasValuesLength(getUserOptions) && (
              <div className="row align-items-center mb-3 g-0">
                <div className="col-2">
                  <label className="form-label mb-2">
                    {translate.Validator}
                  </label>
                </div>
                <div className="col-4 pe-2">
                  <Dropdown
                    optionLabel={translate.NotAssigned}
                    onChange={taskFieldChange("validatorUserID")}
                    items={getUserOptions}
                    defaultValue={taskEdit.validatorUserID}
                    readonly={readonly}
                  ></Dropdown>
                </div>
              </div>
            )}
          <hr className="my-3" />
          <div className="row mb-3 g-0">
            <div className="col-12">
              <label className="form-label mb-2">{translate.Description}</label>
              <TextAreaBigEditor
                id="description"
                onChange={requiredManager.makeRequiredWithId(
                  (value: string) => taskFieldChange("description")(value),
                  "description"
                )}
                placeholder={translate.Description + "..."}
                defaultValue={taskEdit.description}
                disabled={readonly}
              />
              <ValidationMessage
                onChange={requiredManager.makeRequiredWithId(
                  (value: string) => taskFieldChange("description")(value),
                  "description"
                )}
                validationMethod={(value: string | undefined) =>
                  value !== undefined && value.length > 0
                }
                defaultValue={taskEdit.description}
              />
            </div>
          </div>

          <div className="row align-items-center mb-3 g-0">
            <div className="col-auto">
              <SwitchButton
                label={TranslationService.translate.SendEmailReminder}
                onChange={(e: any) => {
                  taskFieldChange("rememberDays")(taskEdit.rememberDays);
                  taskFieldChange("rememberHour")(taskEdit.rememberHour);
                  taskFieldChange("rememberMe")(e);
                }}
                defaultValue={taskEdit.rememberMe}
                disabled={readonly}
              />
            </div>
            {taskEdit.rememberMe && (
              <>
                <div className="col-auto px-2">
                  <label className="form-label mb-2">{translate.Days}</label>
                </div>
                <div className="col-3">
                  <TextEditorNumber
                    id="rememberDays"
                    defaultValue={taskEdit.rememberDays}
                    onChange={(value) => {
                      if (value) {
                        taskFieldChange("rememberDays")(parseInt(value));
                      } else {
                        taskFieldChange("rememberDays")(undefined);
                      }
                    }}
                    disabled={readonly}
                  />
                  <ValidationMessage
                    onChange={requiredManager.makeRequiredWithId(
                      (value: number) => taskFieldChange("rememberDays")(value),
                      "rememberDays"
                    )}
                    defaultValue={taskEdit.rememberDays}
                    validationMethod={(value: number | undefined) => {
                      return (value ?? 0) > 0;
                    }}
                  />
                </div>

                {hasValuesLength(getReminderHourOptions) && (
                  <>
                    <div className="col-auto px-2">
                      <label className="form-label mb-2">
                        {translate.Hour}
                      </label>
                    </div>
                    <div className="col">
                      <Dropdown
                        onChange={taskFieldChange("rememberHour")}
                        items={getReminderHourOptions}
                        defaultValue={taskEdit.rememberHour}
                        readonly={readonly}
                      ></Dropdown>
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* seccion periodo */}
          <div className="row align-items-center mb-3 g-0">
            <div className="col-2">
              <label className="form-label mb-2">{translate.Period}</label>
            </div>
            <div className="col-4 pe-2">
              <Dropdown
                onChange={taskFieldChange("repeatPeriod")}
                items={periodsList}
                defaultValue={taskEdit.repeatPeriod}
                readonly={readonly}
              />
            </div>
            {taskEdit.repeatPeriod === 1 && (
              <div className="col-4 pe-2">
                <Dropdown
                  onChange={taskFieldChange("weekday")}
                  items={weekend}
                  defaultValue={taskEdit.weekday}
                  readonly={readonly}
                />
              </div>
            )}
            {taskEdit.repeatPeriod === 2 && (
              <div className="col-4 pe-2">
                <Dropdown
                  onChange={taskFieldChange("monthday")}
                  items={dayList}
                  defaultValue={taskEdit.monthday}
                  readonly={readonly}
                />
              </div>
            )}
          </div>

          <div className="row align-items-center mb-3 g-0">
            <div className="col-2">
              <label className="form-label mb-2">
                {translate.ExpirationDay}
              </label>
            </div>
            <div className="col-4">
              <HasValidationEditor
                {...new HasValidationEditorProps(
                  DatePickerEditor,
                  (newDate) =>
                    taskFieldChange("calendar")(formatDateDigitsMin(newDate)),
                  <p className="text-danger mb-0">{translate.RequiredField}</p>,
                  undefined,
                  taskEdit?.calendar
                    ? MapIfHasHours(
                        new Date(taskEdit.calendar),
                        restTimezone
                      )!.toString()
                    : new Date().toString(),
                  validationFunctionArray,
                  { disabled: readonly }
                )}
              />
            </div>
          </div>
        </div>

        <FloatingPanelFooter>
          {taskEdit.scheduledTaskID !== undefined &&
            taskEdit.scheduledTaskID > 0 &&
            CompanyService.canDo("deletetask") && (
              <button className="btn btn-danger me-auto" onClick={onDelete}>
                {translate.Delete}
                {isSaving && (
                  <i className="fas fa-spinner-third fa-spin third ms-2"></i>
                )}
              </button>
            )}
          {(CompanyService.canDo("edittask") ||
            (CompanyService.canDo("newtask") && !taskEdit.scheduledTaskID)) && (
            <button className="btn btn-primary me-3" onClick={saveTask}>
              {translate.Save}
              {isSaving && (
                <i className="fas fa-spinner-third fa-spin third ms-2"></i>
              )}
            </button>
          )}
        </FloatingPanelFooter>
      </>
    );
  }
);

export default NewScheduledTaskForm;
